import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { motion } from 'framer-motion';
import { 
  Calendar, Plus, MapPin, Clock, Users, Share2, Search,
  ChevronRight, Sparkles, X, ThumbsUp, ThumbsDown, MessageCircle,
  Send, Check, Copy, Link2, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const timeSlots = [
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
  '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
  '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM'
];

// Pre-built itinerary templates
const ITINERARY_TEMPLATES = [
  {
    id: 'date-night',
    title: 'Romantic Date Night',
    description: 'Perfect evening for two with dinner and entertainment',
    icon: '💕',
    color: 'from-pink-500 to-rose-500',
    suggestedItems: [
      { time: '6:00 PM', type: 'venue', activity: 'Cocktails at a rooftop bar' },
      { time: '7:30 PM', type: 'venue', activity: 'Dinner at an upscale restaurant' },
      { time: '9:30 PM', type: 'event', activity: 'Live jazz or entertainment' }
    ]
  },
  {
    id: 'birthday-crawl',
    title: 'Birthday Bar Crawl',
    description: 'Celebrate with friends hopping between RiNo\'s best spots',
    icon: '🎂',
    color: 'from-purple-500 to-indigo-500',
    suggestedItems: [
      { time: '5:00 PM', type: 'venue', activity: 'Start with appetizers & drinks' },
      { time: '7:00 PM', type: 'venue', activity: 'Brewery stop for craft beers' },
      { time: '9:00 PM', type: 'venue', activity: 'Dance floor or late-night venue' },
      { time: '11:00 PM', type: 'venue', activity: 'Final destination celebration' }
    ]
  },
  {
    id: 'art-culture',
    title: 'Art & Culture Day',
    description: 'Explore RiNo\'s vibrant art scene and local culture',
    icon: '🎨',
    color: 'from-amber-500 to-orange-500',
    suggestedItems: [
      { time: '12:00 PM', type: 'venue', activity: 'Brunch at a local favorite' },
      { time: '2:00 PM', type: 'event', activity: 'Gallery walk or art event' },
      { time: '5:00 PM', type: 'experience', activity: 'Creative workshop or tasting' },
      { time: '7:00 PM', type: 'venue', activity: 'Dinner with live entertainment' }
    ]
  },
  {
    id: 'foodies',
    title: 'Foodie Adventure',
    description: 'A culinary journey through Denver\'s best bites',
    icon: '🍽️',
    color: 'from-green-500 to-emerald-500',
    suggestedItems: [
      { time: '11:30 AM', type: 'venue', activity: 'Coffee & pastries' },
      { time: '1:00 PM', type: 'experience', activity: 'Food tour or cooking class' },
      { time: '4:00 PM', type: 'venue', activity: 'Happy hour small plates' },
      { time: '7:30 PM', type: 'venue', activity: 'Chef\'s tasting dinner' }
    ]
  },
  {
    id: 'weekend-vibes',
    title: 'Weekend Vibes',
    description: 'Relaxed weekend exploring the neighborhood',
    icon: '☀️',
    color: 'from-sky-500 to-blue-500',
    suggestedItems: [
      { time: '10:00 AM', type: 'venue', activity: 'Late breakfast or brunch' },
      { time: '1:00 PM', type: 'venue', activity: 'Day drinking & games' },
      { time: '4:00 PM', type: 'event', activity: 'Live music or event' },
      { time: '7:00 PM', type: 'venue', activity: 'Casual dinner' }
    ]
  },
  {
    id: 'custom',
    title: 'Start from Scratch',
    description: 'Build your own custom itinerary',
    icon: '✨',
    color: 'from-gray-600 to-gray-700',
    suggestedItems: []
  }
];

const TemplateCard = ({ template, onSelect, selected }) => (
  <button
    onClick={() => onSelect(template)}
    className={cn(
      "w-full p-4 rounded-xl border-2 text-left transition-all duration-200",
      selected 
        ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30 ring-2 ring-indigo-600/20" 
        : "border-gray-200 dark:border-gray-700 hover:border-indigo-300 hover:bg-gray-50 dark:hover:bg-gray-800/50"
    )}
    data-testid={`template-${template.id}`}
  >
    <div className="flex items-start gap-3">
      <div className={cn(
        "w-12 h-12 rounded-lg flex items-center justify-center text-2xl bg-gradient-to-br",
        template.color
      )}>
        {template.icon}
      </div>
      <div className="flex-1">
        <h4 className="font-bold text-foreground">{template.title}</h4>
        <p className="text-sm text-muted-foreground">{template.description}</p>
        {template.suggestedItems.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {template.suggestedItems.map((item, i) => (
              <Badge key={i} variant="secondary" className="text-xs">
                {item.time}
              </Badge>
            ))}
          </div>
        )}
      </div>
      {selected && (
        <Check className="w-5 h-5 text-indigo-600" />
      )}
    </div>
  </button>
);

const ItemCard = ({ item, onRemove, onVote, onComment, currentUserId }) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  
  const upVotes = item.votes_up?.length || 0;
  const downVotes = item.votes_down?.length || 0;
  const hasVotedUp = item.votes_up?.includes(currentUserId);
  const hasVotedDown = item.votes_down?.includes(currentUserId);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="relative"
    >
      <Card className="border-l-4 border-l-indigo-600">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center text-indigo-600 font-bold text-sm">
                {item.time_slot?.split(' ')[0]}
              </div>
              <div>
                <h4 className="font-bold text-foreground">{item.item_title}</h4>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <MapPin className="w-3 h-3" />
                  {item.venue_name}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs capitalize">
                    {item.item_type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Added by {item.added_by_name}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 border rounded-full px-2 py-1">
                <button
                  onClick={() => onVote(item.id, 'up')}
                  className={cn(
                    "p-1 rounded-full transition-colors",
                    hasVotedUp ? "text-green-600 bg-green-100" : "text-muted-foreground hover:text-green-600"
                  )}
                >
                  <ThumbsUp className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium min-w-[20px] text-center">{upVotes - downVotes}</span>
                <button
                  onClick={() => onVote(item.id, 'down')}
                  className={cn(
                    "p-1 rounded-full transition-colors",
                    hasVotedDown ? "text-red-600 bg-red-100" : "text-muted-foreground hover:text-red-600"
                  )}
                >
                  <ThumbsDown className="w-4 h-4" />
                </button>
              </div>
              
              <button
                onClick={() => setShowComments(!showComments)}
                className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <MessageCircle className="w-4 h-4" />
                {item.comments?.length > 0 && (
                  <span className="ml-1 text-xs">{item.comments.length}</span>
                )}
              </button>
              
              <button
                onClick={() => onRemove(item.id)}
                className="p-2 rounded-full text-muted-foreground hover:text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {showComments && (
            <div className="mt-4 pt-4 border-t">
              <div className="space-y-2 mb-3">
                {item.comments?.map((comment) => (
                  <div key={comment.id} className="bg-muted p-2 rounded-lg text-sm">
                    <span className="font-medium">{comment.user_name}: </span>
                    {comment.text}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newComment) {
                      onComment(item.id, newComment);
                      setNewComment('');
                    }
                  }}
                />
                <Button
                  size="icon"
                  onClick={() => {
                    if (newComment) {
                      onComment(item.id, newComment);
                      setNewComment('');
                    }
                  }}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const AddItemDialog = ({ isOpen, onClose, onAdd, events, venues, vouchers }) => {
  const [selectedType, setSelectedType] = useState('event');
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedTime, setSelectedTime] = useState('7:00 PM');
  const [search, setSearch] = useState('');

  const getItems = () => {
    switch (selectedType) {
      case 'event':
        return events.filter(e => e.title.toLowerCase().includes(search.toLowerCase()));
      case 'venue':
        return venues.filter(v => v.name.toLowerCase().includes(search.toLowerCase()));
      case 'experience':
        return vouchers.filter(v => v.title.toLowerCase().includes(search.toLowerCase()));
      default:
        return [];
    }
  };

  const handleAdd = () => {
    if (!selectedItem) {
      toast.error('Please select an item');
      return;
    }
    onAdd({
      item_type: selectedType,
      item_id: selectedItem,
      time_slot: selectedTime
    });
    setSelectedItem('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add to Itinerary</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          <Tabs value={selectedType} onValueChange={setSelectedType}>
            <TabsList className="grid grid-cols-3">
              <TabsTrigger value="event">Events</TabsTrigger>
              <TabsTrigger value="venue">Venues</TabsTrigger>
              <TabsTrigger value="experience">Experiences</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="max-h-48 overflow-y-auto space-y-2">
            {getItems().map((item) => {
              const id = item.id;
              const title = selectedType === 'venue' ? item.name : item.title;
              const subtitle = selectedType === 'venue' ? item.address : item.venue_name;
              
              return (
                <button
                  key={id}
                  onClick={() => setSelectedItem(id)}
                  className={cn(
                    "w-full p-3 rounded-lg border text-left transition-colors",
                    selectedItem === id
                      ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-950"
                      : "hover:border-gray-300"
                  )}
                >
                  <p className="font-medium">{title}</p>
                  <p className="text-sm text-muted-foreground">{subtitle}</p>
                </button>
              );
            })}
          </div>

          <div className="space-y-2">
            <Label>Time</Label>
            <Select value={selectedTime} onValueChange={setSelectedTime}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-48">
                {timeSlots.map((time) => (
                  <SelectItem key={time} value={time}>{time}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleAdd}>
              Add to Plan
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ShareDialog = ({ itinerary, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const shareUrl = `${window.location.origin}/itinerary/${itinerary?.share_token}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const inviteByEmail = async () => {
    if (!email) return;
    try {
      await api.post(`/itineraries/${itinerary.id}/invite`, null, {
        params: { email }
      });
      toast.success('Invitation sent!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to send invitation');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Itinerary
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 pt-4">
          <div className="space-y-2">
            <Label>Share link</Label>
            <div className="flex gap-2">
              <Input value={shareUrl} readOnly className="bg-muted" />
              <Button onClick={copyLink}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Anyone with this link can view and suggest changes
            </p>
          </div>

          <div className="space-y-2">
            <Label>Invite by email</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="friend@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button onClick={inviteByEmail}>
                <Mail className="w-4 h-4 mr-2" />
                Invite
              </Button>
            </div>
          </div>

          <div className="pt-4 border-t">
            <h4 className="font-medium mb-3">Collaborators ({itinerary?.collaborators?.length || 0})</h4>
            <div className="space-y-2">
              {itinerary?.collaborators?.map((collab, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                      {collab.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{collab.name}</p>
                      <p className="text-xs text-muted-foreground">{collab.email}</p>
                    </div>
                  </div>
                  <Badge variant="secondary">{collab.role}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ItineraryBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const [date, setDate] = useState(addDays(new Date(), 1));
  const [title, setTitle] = useState('');
  const [itinerary, setItinerary] = useState(null);
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [step, setStep] = useState('template'); // 'template' | 'details'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, venuesRes, vouchersRes] = await Promise.all([
        api.get('/events'),
        api.get('/venues'),
        api.get('/vouchers')
      ]);
      setEvents(eventsRes.data);
      setVenues(venuesRes.data);
      setVouchers(vouchersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createItinerary = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to create an itinerary');
      navigate('/auth');
      return;
    }

    if (!title) {
      toast.error('Please enter a title');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/itineraries', {
        title,
        date: format(date, 'yyyy-MM-dd')
      });
      
      const newItinerary = response.data;
      
      // If a template was selected (not custom), add suggested items
      if (selectedTemplate && selectedTemplate.id !== 'custom' && selectedTemplate.suggestedItems.length > 0) {
        // Find matching items from our data for the template suggestions
        for (const suggestion of selectedTemplate.suggestedItems) {
          let itemToAdd = null;
          
          if (suggestion.type === 'venue' && venues.length > 0) {
            // Pick a random venue
            itemToAdd = venues[Math.floor(Math.random() * venues.length)];
            if (itemToAdd) {
              try {
                await api.post(`/itineraries/${newItinerary.id}/items`, {
                  item_type: 'venue',
                  item_id: itemToAdd.id,
                  time_slot: suggestion.time,
                  notes: suggestion.activity
                });
              } catch (e) {
                console.log('Could not add template item:', e);
              }
            }
          } else if (suggestion.type === 'event' && events.length > 0) {
            itemToAdd = events[Math.floor(Math.random() * events.length)];
            if (itemToAdd) {
              try {
                await api.post(`/itineraries/${newItinerary.id}/items`, {
                  item_type: 'event',
                  item_id: itemToAdd.id,
                  time_slot: suggestion.time,
                  notes: suggestion.activity
                });
              } catch (e) {
                console.log('Could not add template item:', e);
              }
            }
          } else if (suggestion.type === 'experience' && vouchers.length > 0) {
            itemToAdd = vouchers[Math.floor(Math.random() * vouchers.length)];
            if (itemToAdd) {
              try {
                await api.post(`/itineraries/${newItinerary.id}/items`, {
                  item_type: 'experience',
                  item_id: itemToAdd.id,
                  time_slot: suggestion.time,
                  notes: suggestion.activity
                });
              } catch (e) {
                console.log('Could not add template item:', e);
              }
            }
          }
        }
        
        // Refresh the itinerary to get the added items
        const refreshed = await api.get(`/itineraries/${newItinerary.id}`);
        setItinerary(refreshed.data);
        toast.success(`Itinerary created with ${selectedTemplate.title} template!`);
      } else {
        setItinerary(newItinerary);
        toast.success('Itinerary created!');
      }
    } catch (error) {
      toast.error('Failed to create itinerary');
    } finally {
      setCreating(false);
    }
  };

  const addItem = async (itemData) => {
    if (!itinerary) return;
    try {
      const response = await api.post(`/itineraries/${itinerary.id}/items`, itemData);
      setItinerary({
        ...itinerary,
        items: [...(itinerary.items || []), response.data.item]
      });
      toast.success('Added to itinerary!');
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const removeItem = async (itemId) => {
    if (!itinerary) return;
    try {
      await api.delete(`/itineraries/${itinerary.id}/items/${itemId}`);
      setItinerary({
        ...itinerary,
        items: itinerary.items.filter(i => i.id !== itemId)
      });
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const voteItem = async (itemId, voteType) => {
    if (!itinerary || !isAuthenticated) return;
    try {
      await api.post(`/itineraries/${itinerary.id}/items/${itemId}/vote`, null, {
        params: { vote_type: voteType }
      });
      // Refresh itinerary
      const response = await api.get(`/itineraries/${itinerary.id}`);
      setItinerary(response.data);
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const commentItem = async (itemId, comment) => {
    if (!itinerary || !isAuthenticated) return;
    try {
      await api.post(`/itineraries/${itinerary.id}/items/${itemId}/comment`, null, {
        params: { comment }
      });
      // Refresh itinerary
      const response = await api.get(`/itineraries/${itinerary.id}`);
      setItinerary(response.data);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const sortedItems = itinerary?.items?.sort((a, b) => {
    const timeA = timeSlots.indexOf(a.time_slot);
    const timeB = timeSlots.indexOf(b.time_slot);
    return timeA - timeB;
  }) || [];

  return (
    <ConsumerLayout hideFooter>

      {/* Hero */}
      <section className="relative py-16 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581378670696-6bb27d6e450b?crop=entropy&cs=srgb&fm=jpg&q=85')] bg-cover bg-center" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className="bg-white/20 text-white border-0 mb-4">
              <Sparkles className="w-3 h-3 mr-1" />
              Collaborative Planning
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Plan Your Perfect Night
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Build an itinerary, invite friends, and vote on activities together. 
              The best nights are planned together!
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!itinerary ? (
          /* Create New Itinerary */
          <div className="max-w-2xl mx-auto">
            {step === 'template' ? (
              /* Template Selection Step */
              <Card data-testid="template-selection">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    Choose a Template
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Pick a template to get started with suggested activities, or start from scratch.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {ITINERARY_TEMPLATES.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        selected={selectedTemplate?.id === template.id}
                        onSelect={setSelectedTemplate}
                      />
                    ))}
                  </div>

                  <Button 
                    className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 mt-4"
                    onClick={() => {
                      if (!selectedTemplate) {
                        toast.error('Please select a template');
                        return;
                      }
                      // Pre-fill title based on template
                      if (selectedTemplate.id !== 'custom' && !title) {
                        setTitle(selectedTemplate.title);
                      }
                      setStep('details');
                    }}
                    disabled={!selectedTemplate}
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ) : (
              /* Details Step */
              <Card data-testid="itinerary-details">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <button 
                      onClick={() => setStep('template')}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      ← Back to templates
                    </button>
                  </div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                    Plan Your {selectedTemplate?.title || 'Night'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedTemplate && selectedTemplate.id !== 'custom' && (
                    <div className={cn(
                      "p-4 rounded-lg bg-gradient-to-br text-white",
                      selectedTemplate.color
                    )}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{selectedTemplate.icon}</span>
                        <span className="font-bold">{selectedTemplate.title}</span>
                      </div>
                      <p className="text-sm opacity-90">{selectedTemplate.description}</p>
                      {selectedTemplate.suggestedItems.length > 0 && (
                        <div className="mt-3 text-xs opacity-75">
                          We'll add {selectedTemplate.suggestedItems.length} suggested stops based on this template
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Give your night a name</Label>
                    <Input
                      placeholder="e.g., Sarah's Birthday Crawl, Date Night, Weekend Vibes..."
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      data-testid="itinerary-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>When?</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="itinerary-date-picker">
                          <Calendar className="w-4 h-4 mr-2" />
                          {format(date, 'EEEE, MMMM d, yyyy')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={date}
                          onSelect={(d) => d && setDate(d)}
                          disabled={(d) => d < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Button 
                    className="w-full h-12 bg-indigo-600 hover:bg-indigo-700"
                    onClick={createItinerary}
                    disabled={creating || !title}
                    data-testid="create-itinerary-btn"
                  >
                    {creating ? 'Creating...' : 'Start Building'}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>

                  {!isAuthenticated && (
                    <p className="text-sm text-center text-muted-foreground">
                      <button
                        className="text-indigo-600 hover:underline"
                        onClick={() => navigate('/auth')}
                      >
                        Sign in
                      </button>
                      {' '}to save and share your itinerary
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* Itinerary Builder */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{itinerary.title}</h2>
                  <p className="text-muted-foreground flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(itinerary.date), 'EEEE, MMMM d, yyyy')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowShareDialog(true)}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Button 
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => setShowAddDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stop
                  </Button>
                </div>
              </div>

              {/* Timeline */}
              {sortedItems.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-16 text-center">
                    <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-bold mb-2">Your itinerary is empty</h3>
                    <p className="text-muted-foreground mb-4">
                      Add events, venues, and experiences to plan your perfect night
                    </p>
                    <Button onClick={() => setShowAddDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Stop
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {sortedItems.map((item, index) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      onRemove={removeItem}
                      onVote={voteItem}
                      onComment={commentItem}
                      currentUserId={user?.id}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Collaborators */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Collaborators
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {itinerary.collaborators?.map((collab, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                          {collab.name?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{collab.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{collab.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full mt-4"
                    onClick={() => setShowShareDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Invite Friends
                  </Button>
                </CardContent>
              </Card>

              {/* Activity */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {itinerary.activity_log?.slice(-5).reverse().map((activity, i) => (
                      <div key={i} className="text-sm">
                        <p className="text-foreground">
                          <span className="font-medium">{activity.user_name}</span>
                          {' '}
                          {activity.action.replace(/_/g, ' ')}
                          {activity.item_title && ` "${activity.item_title}"`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(activity.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Add */}
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/50 dark:to-purple-950/50 border-indigo-200">
                <CardContent className="p-4 text-center">
                  <Sparkles className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <p className="font-medium mb-2">Need inspiration?</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/events')}
                  >
                    Browse Events
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddItemDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={addItem}
        events={events}
        venues={venues}
        vouchers={vouchers}
      />
      
      <ShareDialog
        itinerary={itinerary}
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
      />
    </ConsumerLayout>
  );
};

export default ItineraryBuilder;
